// ---------------------------------------------------------------------------
// PaymentMethodVisual -- Renders the visual (brand logos or abstract icon)
// for a single payment method. Shared between the public Preise page and the
// order wizard so both surfaces stay in sync.
//
// Brand logos win where available (PayPal, Visa, Mastercard, Vorkasse/prepay).
// Abstract Tabler icons are used for conceptual methods that don't have a
// brand (Credits, Mixed).
//
// This component is "neutral" -- no "use client" directive -- so it works in
// either server or client subgraphs.
// ---------------------------------------------------------------------------

import {
  IconArrowsSplit,
  IconCoins,
  IconCreditCard,
  IconFileInvoice,
} from "@tabler/icons-react";
import Image from "next/image";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

// ---- Logo registry --------------------------------------------------------
//
// Single source of truth for logo slug -> public image path. Both the public
// marketing list (catalog-driven) and the order wizard (hardcoded mapping)
// look up logos here, which keeps the visuals identical across surfaces.
//
// Logos that aren't in /public/images/logos are commented out below; when a
// matching asset is added, uncomment the entry and they will appear in any
// surface that already requests the slug.

const LOGO_PATHS: Record<string, string> = {
  paypal: "/images/logos/paypal.svg",
  prepay: "/images/logos/prepay.svg",
  visa: "/images/logos/visa.svg",
  mastercard: "/images/logos/mastercard.svg",
};

// ---- Default abstract icons by slug ---------------------------------------

const DEFAULT_ICONS: Record<string, ReactNode> = {
  online_payment: <IconCreditCard className="size-full" />,
  credits: <IconCoins className="size-full" />,
  credits_only: <IconCoins className="size-full" />,
  monthly_billing: <IconFileInvoice className="size-full" />,
  mixed: <IconArrowsSplit className="size-full" />,
};

// ---- Sizing ---------------------------------------------------------------

type Size = "sm" | "md";

// `sm` -- fits inside the wizard's size-10 (40px) icon container.
// `md` -- larger inline marketing rendering on the Preise page.
const LOGO_SIZE_CLASSES: Record<Size, string> = {
  sm: "h-5 w-auto max-w-10",
  md: "h-8 w-auto max-w-20",
};

const ICON_SIZE_CLASSES: Record<Size, string> = {
  sm: "size-5",
  md: "size-12",
};

// ---- Props ----------------------------------------------------------------

export interface PaymentMethodVisualMethod {
  /** Catalog or wizard slug (e.g. "online_payment", "credits"). */
  slug: string;
  /** Logo slugs to render (e.g. ["paypal", "visa", "mastercard"]). */
  logos: string[];
  /** Override the default icon used when `logos` is empty. */
  iconOverride?: ReactNode;
}

export interface PaymentMethodVisualProps {
  method: PaymentMethodVisualMethod;
  size?: Size;
  className?: string;
}

// ---- Component ------------------------------------------------------------

export default function PaymentMethodVisual({
  method,
  size = "md",
  className,
}: PaymentMethodVisualProps) {
  const { slug, logos, iconOverride } = method;

  // Pay-over-invoice should always use the same invoice mark as the footer,
  // even if a caller omits the explicit `prepay` logo slug.
  const logoSlugs =
    slug === "pay_over_invoice" && logos.length === 0 ? ["prepay"] : logos;

  // Resolve known logos; skip unknown slugs gracefully.
  const resolvedLogos = logoSlugs
    .map((logoSlug) => ({ slug: logoSlug, src: LOGO_PATHS[logoSlug] }))
    .filter((entry): entry is { slug: string; src: string } =>
      Boolean(entry.src),
    );

  if (resolvedLogos.length > 0) {
    if (size === "sm" && resolvedLogos.length > 1) {
      return (
        <div
          className={cn("relative size-8 shrink-0 overflow-hidden", className)}
        >
          {resolvedLogos.map(({ slug: logoSlug, src }, index) => (
            <Image
              key={logoSlug}
              src={src}
              alt={logoSlug}
              width={80}
              height={32}
              className={cn(
                "absolute object-contain",
                index === 0
                  ? "top-1 left-1/2 h-3 w-7 -translate-x-1/2"
                  : "bottom-0.5 left-1/2 h-3.5 w-6 -translate-x-1/2",
              )}
              unoptimized
            />
          ))}
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex items-center justify-center gap-2",
          size === "sm" ? "gap-1" : "gap-3",
          className,
        )}
      >
        {resolvedLogos.map(({ slug: logoSlug, src }) => {
          const compactInvoice = size === "sm" && logoSlug === "prepay";
          return (
            <Image
              key={logoSlug}
              src={src}
              alt={logoSlug}
              width={120}
              height={48}
              className={cn(
                "object-contain",
                compactInvoice
                  ? "h-7 w-7 max-w-7"
                  : logoSlug === "prepay" && size === "md"
                    ? "h-14 w-20 max-w-20"
                    : LOGO_SIZE_CLASSES[size],
              )}
              unoptimized
            />
          );
        })}
      </div>
    );
  }

  const icon = iconOverride ?? DEFAULT_ICONS[slug] ?? null;
  if (!icon) return null;

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center",
        ICON_SIZE_CLASSES[size],
        className,
      )}
      aria-hidden="true"
    >
      {icon}
    </span>
  );
}
